<template>
    <Tabs class="h-full" value="parsed">
        <Message class="m-2" variant="simple">
            <template #icon>
                <i class="i-ph-info"/>
            </template>
            EHF har ikke vedlegg. Visningen autogenereres basert på EHF-data.
        </Message>
        <TabList class="shrink-0">
            <Tab value="parsed">Autogenerert info</Tab>
            <Tab value="raw">Rådata</Tab>
        </TabList>
        <TabPanels class="h-full">
            <TabPanel value="parsed">
                <div class="flex flex-col">
                    <template v-if="sender">
                        <Title>{{ sender.name }}</Title>
                        <SubTitle class="mb-4">{{ sender.orgNo }}</SubTitle>
                    </template>
                    <key-value v-if="incomingDocument.invoiceDraft.description" name="Beskrivelse" :value="incomingDocument.invoiceDraft.description"/>
                    <div class="grid grid-cols-3 gap-4 mb-4">
                        <key-value v-if="draft.invoiceNo" name="Fakturanummer" :value="draft.invoiceNo"/>
                        <key-value v-if="draft.customerReference" name="Kunderef." :value="draft.customerReference"/>
                        <key-value v-if="draft.customerIdentification" name="KID" :value="draft.customerIdentification"/>
                        <key-value v-if="draft.date" name="Fakturadato" :value="draft.date"/>
                        <key-value v-if="draft.dueDate" name="Forfallsdato" :value="draft.dueDate"/>
                        <key-value v-if="draft.bankAccount" name="Bankkonto" :value="draft.bankAccount"/>
                        <key-value v-if="draft.grossAmount" name="Bruttobeløp" :value="draft.grossAmount"/>
                        <key-value v-if="draft.netAmount" name="Nettobeløp" :value="draft.netAmount"/>
                        <key-value v-if="draft.deliveryLocation" name="Leveringssted" :value="draft.deliveryLocation"/>
                    </div>

                    <DataTable :value="draft.productLines" class="w-full">
                        <Column field="name" header="Navn / Besk.">
                            <template #body="slotProps">
                                <div>
                                    <div>{{ slotProps.data.name }}</div>
                                    <div class="text-sm text-gray-500">{{ slotProps.data.description }}</div>
                                </div>
                            </template>
                        </Column>
                        <Column field="quanity">
                            <template #header>
                                <div class="text-right font-bold w-full">
                                    Antall
                                </div>
                            </template>
                            <template #body="slotProps">
                                <div class="text-right">
                                    {{ slotProps.data.quanity }}
                                </div>
                            </template>
                        </Column>
                        <Column field="discount">
                            <template #header>
                                <div class="text-right font-bold w-full">
                                    Avslag
                                </div>
                            </template>
                            <template #body="slotProps">
                                <div class="text-right">
                                    {{ slotProps.data.discount * 100 }}%
                                </div>
                            </template>
                        </Column>
                        <Column field="unitPrice">
                            <template #header>
                                <div class="text-right font-bold w-full">
                                    Enh.pris
                                </div>
                            </template>
                            <template #body="slotProps">
                                <div class="text-right">
                                    {{ formatCurrency(slotProps.data.unitPrice) }}
                                </div>
                            </template>
                        </Column>
                        <Column field="vatRate" header-class="text-right">
                            <template #header>
                                <div class="text-right font-bold w-full">
                                    MVA
                                </div>
                            </template>
                            <template #body="slotProps">
                                <div class="text-right">
                                    {{ slotProps.data.vatRate * 100 }}%
                                </div>
                            </template>
                        </Column>
                        <Column field="grossAmount">
                            <template #header>
                                <div class="text-right font-bold w-full">
                                    Brutto
                                </div>
                            </template>
                            <template #body="slotProps">
                                <div class="text-right">
                                    {{ formatCurrency(slotProps.data.grossAmount) }}
                                </div>
                            </template>
                        </Column>
                        <Column>
                            <template #header>
                                <div class="text-right font-bold w-full">
                                    Netto
                                </div>
                            </template>
                            <template #body="slotProps">
                                <div class="text-right">
                                    {{ formatCurrency(slotProps.data.netAmount) }}
                                </div>
                            </template>
                        </Column>
                    </DataTable>
                </div>
            </TabPanel>
            <TabPanel value="raw" class="h-full">
                <pre class="text-xs h-full overflow-auto">{{ JSON.stringify(data, null, 2) }}</pre>
            </TabPanel>
        </TabPanels>
    </Tabs>
</template>

<script setup lang="ts">

import {IncomingDocument} from '@/types/IncomingDocument'
import {computed, onMounted, PropType, ref, watch} from 'vue'
import {storageResolveUrl} from '@/services/document.service'
import {Tabs, Tab, TabList, TabPanels, TabPanel, Message, DataTable, Column} from 'primevue'
import Title from '@/components/Title.vue'
import SubTitle from '@/components/SubTitle.vue'
import KeyValue from '@/components/KeyValue.vue'
import {formatCurrency} from '@/services/formatter.service'

const props = defineProps({
    incomingDocument: {
        type: Object as PropType<IncomingDocument>,
        required: true
    }
})

const draft = computed(() => {
    return props.incomingDocument.invoiceDraft.draft
})

const sender = computed(() => {
    return draft.value.sender
})

const data = ref({})

const loadDocument = async () => {
    const documentMeta = props.incomingDocument?.documentMeta
    const documentUrl = await storageResolveUrl(documentMeta)
    const response = await fetch(documentUrl)
    data.value = await response.json()
}

onMounted(loadDocument)
watch(() => props.incomingDocument.documentMeta.uuid, loadDocument)

</script>

<style scoped>

</style>